import { useEffect, useRef, useState, ChangeEvent } from 'react';
import { Check, Container, Text } from './index.styles';
import { Icon } from '../../atoms/Icon';

export const initialStates = ['unchecked', 'checked', 'indeterminate'] as const;
export const sizes = ['sm', 'md', 'lg', 'xl'] as const;

export interface CheckboxProps extends Component {
  /**
   * Accessible name for the underlying input, for cases with no visible `label` (e.g. an
   * icon-only selection checkbox). Optional.
   */
  ariaLabel?: string;
  /**
   * Check event emitter. Optional.
   */
  check?: (e: boolean) => void;
  /**
   * Indicates visual error feedback. Optional.
   */
  hasError?: boolean;
  /**
   * ID attribute. Optional.
   */
  id?: string;
  /**
   * Initial check status ("Indeterminate" is readonly). Optional.
   */
  initialState?: (typeof initialStates)[number];
  /**
   * Disable state attribute. Optional.
   */
  isDisabled?: boolean;
  /**
   * Label. Optional.
   */
  label?: string;
  /**
   * Sizing variations. Optional.
   */
  size?: (typeof sizes)[number];
}

/**
 * Checkbox
 */
export const Checkbox = (props: CheckboxProps) => {
  const {
    ariaLabel,
    check,
    initialState = 'unchecked',
    className = '',
    customClass = '',
    hasError,
    id,
    isDisabled,
    label,
    size = 'md',
    tabIndex = 0,
    testId = 'checkbox',
  } = props;

  // 1. Clean React 19 Fix: Initialize local states inline on first pass without using effects
  const [isChecked, setIsChecked] = useState<boolean>(() => initialState === 'checked');
  const [isIndeterminate, setIsIndeterminate] = useState<boolean>(() => initialState === 'indeterminate');
  const ref = useRef<HTMLInputElement | null>(null);

  // 2. React 19 Prop Synchronization: If props alter after mount, derive values directly during render
  const [prevInitialState, setPrevInitialState] = useState(initialState);

  if (prevInitialState !== initialState) {
    setPrevInitialState(initialState);
    setIsChecked(initialState === 'checked');
    setIsIndeterminate(initialState === 'indeterminate');
  }

  // 3. Keep the underlying DOM element properties in sync without causing cascading state calls
  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = isIndeterminate;
      ref.current.checked = isChecked;
    }
  }, [isChecked, isIndeterminate]);

  const handleCheck = (e: boolean) => {
    setIsChecked(e);
    setIsIndeterminate(false);
    if (check) {
      check(e);
    }
  };

  return (
    <Container
      className={`ds-checkbox ds-checkbox-${size} ${customClass} ${className}`}
      data-testid={testId}
      htmlFor={id}
      isDisabled={isDisabled}
      isIndeterminate={isIndeterminate}
      size={size}
    >
      <Check
        hasError={hasError}
        isChecked={isChecked}
        isIndeterminate={isIndeterminate}
        isDisabled={isDisabled}
      >
        {isChecked && <Icon name="check" />}
        {isIndeterminate && <Icon name="minus" />}
        <input
          aria-label={ariaLabel}
          disabled={isDisabled}
          id={id}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleCheck(e.target.checked)}
          ref={ref}
          tabIndex={tabIndex}
          type="checkbox"
        />
      </Check>
      {label && <Text data-testid={`${testId}-label`}>{label}</Text>}
    </Container>
  );
};
